import { gql } from '@apollo/client';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  CardTitle,
} from '@apollo/orbit';
import React from 'react';
import { useHistory, useLocation } from 'react-router';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { useIdentity } from 'src/hooks/useIdentity';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import Config from 'src/lib/config';
import {
  GraphVisibilityCardFragment as GraphVisibilityCardFragmentType,
  UserPermission,
} from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { GraphVisibilityModal } from './graphVisibilityModal/GraphVisibilityModal';

export enum DisabledReason {
  FeatureNotAvailableInAccountTier,
  InsufficientUserPermission,
  WouldLoseAccess,
}

export const GraphVisibilityCard = ({
  dataFragment,
}: {
  dataFragment: GraphVisibilityCardFragmentType;
}) => {
  const graphId = dataFragment.id;
  const accountId = dataFragment.account?.id;

  const { me, memberships, isUser } = useIdentity();
  const [sudo] = useLocalStorage('sudo');
  const wouldLoseAccess =
    !sudo &&
    !dataFragment.hiddenFromUninvitedNonAdminAccountMembers &&
    !dataFragment.roleOverrides?.some(({ user }) => user.id === me?.id) &&
    isUser &&
    memberships.find((membership) => membership.account.id === accountId)
      ?.role !== UserPermission.ORG_ADMIN;

  const userRolesEnabled = !!dataFragment.account?.currentPlan?.userRoles;

  return (
    <GraphVisibilityCardUI
      graphId={graphId}
      wouldLoseAccess={wouldLoseAccess}
      userRolesEnabled={userRolesEnabled}
      hiddenFromUninvitedNonAdminAccountMembers={
        dataFragment.hiddenFromUninvitedNonAdminAccountMembers
      }
    />
  );
};

export const GraphVisibilityCardFragment = gql`
  fragment GraphVisibilityCardFragment on Service {
    id
    hiddenFromUninvitedNonAdminAccountMembers
    account {
      id
      currentPlan {
        id
        userRoles
      }
    }
    roles {
      canManageAccess
    }
    roleOverrides {
      role
      user {
        id
      }
    }
  }
`;

export const GraphVisibilityCardUI = ({
  graphId,
  hiddenFromUninvitedNonAdminAccountMembers,
  userRolesEnabled,
  wouldLoseAccess,
}: {
  graphId: string;
  hiddenFromUninvitedNonAdminAccountMembers: boolean;
  userRolesEnabled: boolean;
  wouldLoseAccess: boolean;
}) => {
  const history = useHistory();
  const location = useLocation();
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            Graph visibility:{' '}
            {hiddenFromUninvitedNonAdminAccountMembers ? 'Hidden' : 'Visible'}
          </CardTitle>
          <CardDescription>
            {hiddenFromUninvitedNonAdminAccountMembers
              ? 'Currently hidden from other org members by default.'
              : 'Currently visible to other org members by default.'}
          </CardDescription>

          <CardHeaderControl>
            <PermissionWarning
              graphId={graphId}
              graphPermissions="canManageAccess"
              childRenderer={(hasPermission) => (
                <Tooltip
                  label="You cannot change the visibility of this graph because you would lose access. If you add a graph role below, you will be able to change this setting"
                  disabled={
                    !hasPermission || !wouldLoseAccess || !userRolesEnabled
                  }
                >
                  <Button
                    variant="destructive"
                    isDisabled={
                      !hasPermission || wouldLoseAccess || !userRolesEnabled
                    }
                    onClick={() => {
                      history.push(
                        setOverlayVisibleLocation({
                          location,
                          overlayName: Config.modals.updateGraphVisibility,
                        }),
                      );
                    }}
                  >
                    Change visibility
                  </Button>
                </Tooltip>
              )}
            />
          </CardHeaderControl>
        </CardHeader>
      </Card>
      <URLModal name={Config.modals.updateGraphVisibility}>
        {({ isOpen, closeModal }) => (
          <GraphVisibilityModal
            isOpen={isOpen}
            graphId={graphId}
            hiddenFromUninvitedNonAdminAccountMembers={
              hiddenFromUninvitedNonAdminAccountMembers
            }
            onClose={closeModal}
          />
        )}
      </URLModal>
    </>
  );
};
